import { useCallback, useEffect, useState } from "react";
import { CalendarDays, History, Loader2, Square } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

// Restaurar as conversas que já estavam no aparelho.
//
// Antes isto só acontecia no instante da conexão: quem esqueceu de marcar
// "importar histórico" antes de ler o QR tinha de desconectar e ler o QR de
// novo — derrubando o atendimento em andamento — para ver o passado.
//
// São duas escolhas, e elas são bem diferentes na prática: descer até o começo
// da conversa pode levar horas e trazer anos de mensagem; parar numa data traz
// só o período que interessa. Por isso a data é a opção que já vem marcada.
//
// A busca roda no servidor: fechar esta janela não interrompe nada, e reabrir
// mostra onde está. Parar é decisão explícita, no botão.
const emDia = (data) => data.toISOString().slice(0, 10);

const RestaurarConversasModal = ({ aberto, aoFechar, conexao }) => {
  const [modo, setModo] = useState("data");
  const [data, setData] = useState(() => {
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 30);
    return emDia(trintaDiasAtras);
  });
  const [estado, setEstado] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const whatsappId = conexao?.id;

  const consultar = useCallback(() => {
    if (!whatsappId) return Promise.resolve();

    return api
      .get(`/whatsapp/${whatsappId}/restore`)
      .then(({ data: atual }) => setEstado(atual))
      .catch(() => {});
  }, [whatsappId]);

  // Ao abrir, saber se já existe uma restauração andando desta conexão — foi
  // ela que ficou rodando depois que a janela fechou.
  useEffect(() => {
    if (!aberto) return undefined;

    consultar();

    const socket = openSocket();
    const onImport = (dados) => {
      if (Number(dados?.whatsappId) !== Number(whatsappId)) return;

      setEstado((anterior) => ({
        ...(anterior || {}),
        ...dados,
        emAndamento: dados.action !== "finished",
      }));
    };
    socket.on("import", onImport);

    return () => {
      try { socket.off("import", onImport); } catch (_) {}
    };
  }, [aberto, consultar, whatsappId]);

  const emAndamento = Boolean(estado?.emAndamento);
  // Ficou marcada no banco, mas nenhum laço está rodando: reinício do backend
  // ou queda da conexão no meio da varredura.
  const pendente = !emAndamento && Boolean(estado?.pendente);

  const começar = async () => {
    setEnviando(true);

    try {
      const { data: novo } = await api.post(`/whatsapp/${whatsappId}/restore`, {
        ate: modo === "data" ? data : null,
      });
      setEstado(novo);
    } catch (err) {
      toastError(err);
    }

    setEnviando(false);
  };

  const parar = async () => {
    try {
      await api.delete(`/whatsapp/${whatsappId}/restore`);
      await consultar();
    } catch (err) {
      toastError(err);
    }
  };

  const opcao = (valor, icone, titulo, descricao) => (
    <button
      type="button"
      onClick={() => setModo(valor)}
      disabled={emAndamento}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors",
        modo === valor
          ? "border-primary bg-primary/5"
          : "hover:bg-muted/60 disabled:hover:bg-transparent",
        emAndamento && "opacity-60",
      )}
    >
      <span
        className={cn(
          "mt-0.5 shrink-0 rounded-md p-1.5",
          modo === valor
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground",
        )}
      >
        {icone}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{titulo}</span>
        <span className="block text-xs text-muted-foreground">{descricao}</span>
      </span>
    </button>
  );

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Restaurar conversas</DialogTitle>
          <DialogDescription>
            Busca no aparelho as mensagens anteriores às que o sistema já tem
            {conexao?.name ? ` em ${conexao.name}` : ""}.
          </DialogDescription>
        </DialogHeader>

        {!conexao?.importHistory && (
          // O despejo de todas as conversas de uma vez só existe no momento em
          // que o QR é lido. Sem dizer isso aqui, a pessoa repete a busca
          // achando que ela falhou.
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-muted-foreground">
            Esta conexão está com a importação de histórico desligada. A busca
            traz o que o aparelho ainda tiver de cada conversa. Para receber o
            histórico completo de todos os contatos de uma vez, ligue "Importar
            histórico do aparelho" na conexão e leia o QR Code de novo.
          </p>
        )}

        <div className="space-y-3">
          {opcao(
            "data",
            <CalendarDays className="h-4 w-4" />,
            "A partir de uma data",
            "Desce até o dia escolhido e para.",
          )}

          {modo === "data" && (
            <div className="ml-11 space-y-1.5">
              <Label htmlFor="restaurar-data" className="text-xs">
                Trazer conversas até
              </Label>
              <Input
                id="restaurar-data"
                type="date"
                value={data}
                max={emDia(new Date())}
                disabled={emAndamento}
                onChange={(e) => setData(e.target.value)}
              />
            </div>
          )}

          {opcao(
            "tudo",
            <History className="h-4 w-4" />,
            "Tudo",
            "Vai até o começo de cada conversa. Pode levar horas.",
          )}
        </div>

        {/* Marcada para retomar, mas sem ninguém tocando agora — o backend foi
            reiniciado, ou a conexão caiu, no meio da varredura. Sem este aviso
            a tela dizia "nada rodando" e o botão recusava começar de novo, sem
            explicar por quê. */}
        {pendente && (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-muted-foreground">
            Havia uma restauração em andamento que ficou pela metade
            {estado?.aguardandoConexao
              ? " e a conexão está fora do ar. Ela recomeça sozinha assim que o número reconectar."
              : ". Ela recomeça sozinha na próxima reconexão."}{" "}
            Use “Cancelar restauração” se não quiser mais.
          </p>
        )}

        {estado && (estado.emAndamento || estado.rodadas > 0) && (
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium">
              {emAndamento ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Buscando no aparelho…
                </>
              ) : (
                "Restauração concluída"
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {estado.mensagens || 0} mensagens recuperadas
              {emAndamento && estado.faltam
                ? ` · ${estado.faltam} de ${estado.conversas} conversas ainda buscando`
                : ""}
              {estado.maisAntiga
                ? ` · a conversa já recuou até ${new Date(
                    estado.maisAntiga,
                  ).toLocaleDateString("pt-BR")}`
                : ""}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>

          {emAndamento || pendente ? (
            <Button variant="destructive" onClick={parar} className="gap-1.5">
              <Square className="h-3.5 w-3.5" />
              {emAndamento ? "Parar" : "Cancelar restauração"}
            </Button>
          ) : (
            <Button
              onClick={começar}
              disabled={enviando || (modo === "data" && !data)}
              className="gap-1.5"
            >
              {enviando ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <History className="h-3.5 w-3.5" />
              )}
              Restaurar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RestaurarConversasModal;
